import { useState, useCallback } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { TwoFactorCodeInput } from '@/components/auth/TwoFactorCodeInput';
import { useAuth } from '@/lib/auth';
import { returnPathFrom } from '@/lib/return-to';
import { ShieldCheck, Loader2 } from 'lucide-react';

interface LocationState {
  pendingToken?: string;
  /** FR-001f: the page to open once signed in, passed on by the login form. */
  from?: string;
  profile?: {
    email: string;
    firstname?: string;
    lastname?: string;
  };
}

interface TwoFAVerifyResponse {
  token: string;
  user: {
    id: number;
    email: string;
    name: string;
    roles: string[];
  };
  remember2faToken?: string;
}

interface ErrorResponse {
  error: string;
  message: string;
}

const REMEMBER_2FA_TOKEN_KEY = 'bridge_remember_2fa_token';

export function TwoFactorVerifyPage(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const state = location.state as LocationState | null;
  const pendingToken = state?.pendingToken;
  const profile = state?.profile;
  const returnTo = returnPathFrom(state);

  const [code, setCode] = useState('');
  const [rememberDevice, setRememberDevice] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = useCallback(async (e: React.SyntheticEvent): Promise<void> => {
    e.preventDefault();

    if (code.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    setError(null);
    setIsLoading(true);

    try {
      const response = await fetch('/api/v1/auth/2fa/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          pendingToken,
          code,
          rememberDevice,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json() as ErrorResponse;
        throw new Error(errorData.message || 'Verification failed');
      }

      const data = await response.json() as TwoFAVerifyResponse;

      // Store remember token if provided
      if (data.remember2faToken) {
        try {
          localStorage.setItem(REMEMBER_2FA_TOKEN_KEY, data.remember2faToken);
        } catch {
          // Ignore storage errors
        }
      }

      // Complete login
      login(data.token);
      void navigate(returnTo, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
      setCode(''); // Clear code on error for retry
    } finally {
      setIsLoading(false);
    }
  }, [code, pendingToken, rememberDevice, login, navigate, returnTo]);

  // Handle code completion (auto-submit when 6 digits entered)
  const handleCodeChange = useCallback((newCode: string): void => {
    setCode(newCode);
    setError(null);
  }, []);

  // Redirect if no pending token
  if (!pendingToken) {
    return <Navigate to="/login" replace state={{ from: returnTo }} />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <Card className="w-full max-w-md" data-testid="2fa-verify-form">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <ShieldCheck className="w-8 h-8 text-primary" />
            </div>
          </div>
          <CardTitle className="text-2xl">Two-Factor Authentication</CardTitle>
          <CardDescription>
            {profile?.email ? (
              <>Enter the verification code for <strong>{profile.email}</strong></>
            ) : (
              <>Enter the 6-digit code from your authenticator app</>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={(e) => { void handleSubmit(e); }} className="space-y-6">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <TwoFactorCodeInput
              value={code}
              onChange={handleCodeChange}
              disabled={isLoading}
              autoFocus
              error={!!error}
            />

            <div className="flex items-center space-x-2">
              <Checkbox
                id="rememberDevice"
                checked={rememberDevice}
                onCheckedChange={(checked: boolean | 'indeterminate') => { setRememberDevice(checked === true); }}
                disabled={isLoading}
                data-testid="2fa-remember-checkbox"
              />
              <Label
                htmlFor="rememberDevice"
                className="text-sm text-muted-foreground cursor-pointer"
              >
                Remember this device for 14 days
              </Label>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={isLoading || code.length !== 6}
              data-testid="2fa-verify-submit-btn"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                'Verify'
              )}
            </Button>

            <div className="text-center">
              <Button
                type="button"
                variant="link"
                className="text-sm text-muted-foreground"
                onClick={() => { void navigate('/login', { replace: true, state: { from: returnTo } }); }}
                disabled={isLoading}
              >
                Use a different account
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export { REMEMBER_2FA_TOKEN_KEY };
